import { MobileTypeEnum } from './loader/pagedata_mobiletype';
import { RawResourceData } from './loader/pagedata_mobile';
import { NinePatchChunk, NinePatchChunkParser } from './android/9patch';

// (ported from resources.go)
// UploadedResourceCanonicalName returns the Datastore key and GCS object name for resources uploaded from the client and hashed with <algorithm>
export function UploadedResourceCanonicalName(algorithm: string, hash: string): string {
  return `upload:${algorithm}:${hash}`;
}
// UploadedResourceCanonicalNameShaEmbedded returns the Datastore key and GCS object name for resources uploaded from the client
export function UploadedResourceCanonicalNameShaEmbedded(hash: string): string {
  return `upload:${hash}`;
}

export const ASSET_FONTS: Set<String> = new Set();

export class Resources {
  private _imageInternalMap: { [id: string]: ImageData } = {};
  private _assetInternalMap: { [id: string]: AssetData } = {};
  private _fontInternalMap: { [id: string]: FontData } = {};
  // only used on iOS, map base file names to potential full paths
  private _iOSImageBaseNameMap: { [id: string]: string[] } = {};
  private _isFailed: boolean;
  private _type: MobileTypeEnum;

  constructor(
    resourceData: RawResourceData,
    private _assetRoot: string,
    private _canonicalRoot: string,
  ) {
    this._type = resourceData.type;

    if (!resourceData || resourceData.failed) {
      this._isFailed = true;
      return;
    }

    this._isFailed = false;

    if (resourceData.type == MobileTypeEnum.Android) {
      Object.keys(resourceData.assets).forEach(key => {
        const assetEntry = resourceData.assets[key];
        this._assetInternalMap[key] = new AssetData(assetEntry);
      });

      Object.keys(resourceData.resources).forEach(key => {
        const resourceEntry = resourceData.resources[key];

        if (resourceEntry.files) {
          Object.keys(resourceEntry.files).forEach(fileKey => {
            const file = resourceEntry.files[fileKey];
            this._imageInternalMap[`id:${fileKey}`] = new ImageData(
              file.name,
              file.np ? NinePatchChunkParser.parse(file.np, true) : null,
              null,
            );
          });
        }
      });
    } else if (resourceData.type == MobileTypeEnum.iOS) {
      Object.keys(resourceData.assets).forEach(key => {
        const assetEntry = resourceData.assets[key];
        if (assetEntry.file) {
          /* Some images don't come from an Assets.car, and are just
           * uploaded as file assets.  This is particularly common for React
           * Native apps (ch139195).
           */
          this._imageInternalMap[assetEntry.file] = new ImageData(assetEntry.name, null, null);
        }
        if (assetEntry.image) {
          const name = assetEntry.asset.name;
          this._imageInternalMap[name] = new ImageData(
            assetEntry.image,
            null,
            assetEntry.asset.image,
          );
          // see comment in lookupImage
          const baseName = (name as string).split('/').pop();
          if (!this._iOSImageBaseNameMap[baseName]) {
            this._iOSImageBaseNameMap[baseName] = [];
          }
          this._iOSImageBaseNameMap[baseName].push(name);
        }
      });
      if (resourceData.fonts) {
        Object.keys(resourceData.fonts).forEach(key => {
          ASSET_FONTS.add(key);
          this._fontInternalMap[key] = new FontData(key, _assetRoot + resourceData.fonts[key]);
        });
      }
    } else if (resourceData.type == MobileTypeEnum.MobileMock) {
      // Currently nothing
    } else {
      // Unknown type, just set failed and bail
      this._isFailed = true;
    }
  }

  type(): MobileTypeEnum {
    return this._type;
  }

  failed(): boolean {
    return this._isFailed;
  }

  lookupAsset(id: number): AssetData {
    // keys are hex strings
    return this._assetInternalMap[id.toString(16)];
  }

  lookupImage(id: string): ImageData {
    if (this._type == MobileTypeEnum.Android) {
      return this._imageInternalMap[id];
    }
    if (this._type == MobileTypeEnum.iOS) {
      let json = JSON.parse(id);
      while (json.derivation) {
        json = json.from;
        if (!json) {
          return null;
        }
      }
      if (!json.asset) /* This came from an 'unknown tracked'. */ return null;
      let img = this._imageInternalMap[json.asset];
      if (img) return img;
      /* Maybe "json.asset" contains a spurious .png at the end? (ch121116) */
      img = this._imageInternalMap[json.asset.replace(/\.png$/, '')];
      if (img) return img;
      /* It's possible for us to lose track of the proper bundle for an asset
        during capture, resulting in us just having a base name instead of
        /Frameworks/$framework_name/$base_name
        Try resolving to a full path and use that image if it's unambiguous */
      const fullNames = this._iOSImageBaseNameMap[json.asset];
      if (fullNames && fullNames.length === 1) {
        img = this._imageInternalMap[fullNames[0]];
      }
      if (img) return img;
      /* Nothing else to try.  Oh, well; fall through. */
    }
    return null;
  }

  lookupParams(id: string): IOSImageParams {
    /* Only iOS has derived image parameters. */
    if (this._type == MobileTypeEnum.iOS) {
      const json = JSON.parse(id);
      const params = new IOSImageParams();
      params.fromDerivation(json);
      const image = this.lookupImage(id);
      if (image) {
        params.intrinsic = image.getiOSExtras();
      }
      return params;
    }
    return null;
  }

  lookupFont(id: string): FontData {
    return this._fontInternalMap[id];
  }

  allFonts(): FontData[] {
    return Object.values(this._fontInternalMap);
  }

  getPathForResourceByHash(hash: string, asset: boolean) {
    // If this has the hashing algorithm embedded, then we don't care if it is an asset or not - they are now in the same place
    if (hash?.startsWith('sha256:')) {
      // The new upload hashing will prepend the sha hash type, so load the files from the org's dir
      return this._canonicalRoot + UploadedResourceCanonicalNameShaEmbedded(hash);
    }
    if (asset) {
      return this._assetRoot + hash;
    } else {
      // This is the old-style hash that doesn't prepend the algorithm, so we know it's sha1
      return this._canonicalRoot + UploadedResourceCanonicalName('sha1', hash);
    }
  }
}

export class AssetData {
  private name: string;
  private file: string;

  constructor(assetData: any) {
    this.name = assetData.name;
    this.file = assetData.file;
  }

  getName(): string {
    return this.name;
  }

  getFile(): string {
    return this.file;
  }
}

class ImageData {
  private name: string;
  private ninePatch: NinePatchChunk;
  private iOSExtras: any;

  constructor(name: string, ninePatch: NinePatchChunk, iOSExtras: any) {
    this.ninePatch = ninePatch;
    this.name = name;
    this.iOSExtras = iOSExtras;
  }

  getName(): string {
    return this.name;
  }

  /* i.e., templateRenderingMode, resizingMode, exifOrientation, ...  -- see
   * FSAssetCollection::dictionaryForImage:.
   *
   * TODO: this should have a type, but it does not come in with one...
   */
  getiOSExtras(): any {
    return this.iOSExtras;
  }

  // TODO(mmastrac): We should defer nine-patch parsing on Android until this image is actually rendered at playback
  getNinePatchChunk(): NinePatchChunk {
    return this.ninePatch;
  }
}

class FontData {
  private name: string;
  private url: string;

  constructor(name: string, url: string) {
    this.name = name;
    this.url = url;
  }

  getName(): string {
    return this.name;
  }

  getUrl(): string {
    return this.url;
  }
}

/* XXX: this should, potentially, be in iOS-specific code. */
export enum IOSRenderingModeEnum {
  Automatic = 0,
  AlwaysOriginal = 1,
  AlwaysTemplate = 2,
}

export enum IOSOrientationEnum {
  Up = 0,
  Down = 1,
  Left = 2,
  Right = 3,
  UpMirrored = 4,
  DownMirrored = 5,
  LeftMirrored = 6,
  RightMirrored = 7,
}

export enum IOSResizingMode {
  Tile = 0,
  Stretch = 1,
}

export enum IOSContentModeEnum {
  ScaleToFill = 0,
  ScaleAspectFit,
  ScaleAspectFill,
  Redraw,
  Center,
  Top,
  Bottom,
  Left,
  Right,
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight,
}

// http://www.verypdf.com/document/pdf-format-reference/pg_0216.htm
export enum IOSLineCapEnum {
  BUTT = 0,
  ROUND = 1,
  SQUARE = 2,
}
export enum IOSLineJoinEnum {
  MITER = 0,
  ROUND = 1,
  BEVEL = 2,
}
export class IOSImageParams {
  renderingMode: IOSRenderingModeEnum = IOSRenderingModeEnum.Automatic;
  scale: number = 1.0;
  orientation: IOSOrientationEnum = IOSOrientationEnum.Up;
  ninePatchOverride: [number];
  stretchMode: IOSResizingMode = IOSResizingMode.Stretch;
  intrinsic: any; /* an iOSExtras -- i.e., intrinsic properties of the image, not of the derivation */

  fromDerivation(spec: any): void {
    /* Head-recurse, so that we override as appropriate within. */
    if (spec.from) {
      this.fromDerivation(spec.from);
    }

    /* Now parse each of the derivations. */
    if (spec.derivation) {
      const copyProps = [
        'renderingMode',
        'scale',
        'orientation',
        'ninePatchOverride' /* XXX: this needs to be plumbed through to 9patch renderer */,
        'stretchMode',
      ];
      const ignoreProps = [
        // The knownSafe flag triggers recording of an image that we would otherwise default to masking.
        // It is (currently) only used for generated border images in React Native apps.
        'knownSafe',
      ];

      Object.keys(spec.derivation).forEach(prop => {
        if (copyProps.indexOf(prop) !== -1) {
          this[prop] = spec.derivation[prop];
        } else if (ignoreProps.indexOf(prop) !== -1) {
          // do nothing
        } else {
          console.log(`warning: unhandled image spec element ${prop}`);
        }
      });
    }
  }
}

export enum IOSBlurEnum {
  // FullStory values
  Unparseable = -2,
  None = -1,

  // Apple values
  ExtraLight = 0,
  Light,
  Dark,
  ExtraDark,
  Regular,
  Prominent,

  SystemUltraThinMaterial,
  SystemThinMaterial,
  SystemMaterial,
  SystemThickMaterial,
  SystemChromeMaterial,

  SystemUltraThinMaterialLight,
  SystemThinMaterialLight,
  SystemMaterialLight,
  SystemThickMaterialLight,
  SystemChromeMaterialLight,

  SystemUltraThinMaterialDark,
  SystemThinMaterialDark,
  SystemMaterialDark,
  SystemThickMaterialDark,
  SystemChromeMaterialDark,

  SystemVibrantBackgroundRegular = 1200,
}

export enum IOSVibrancyEnum {
  // FullStory values
  Unparseable = -2,
  None = -1,

  // Apple values
  Label = 0,
  SecondaryLabel,
  TertiaryLabel,
  QuaternaryLabel,

  Fill,
  SecondaryFill,
  TertiaryFill,

  Separator,
}
